import { Fragment, useEffect, useContext, useState } from 'react'
import PropTypes from 'prop-types'
import { rgba } from 'polished'
import qs from 'qs'
import { useTheme } from '@emotion/react'
import { Button, Container } from 'chaoskit/src/components'
import { isEmpty } from 'lodash'
import useUpdateEffect from 'react-use/lib/useUpdateEffect'
import { tealGreen } from 'chaoskit/src/assets/styles/utility/gradient'
import { misc, text } from 'chaoskit/src/assets/styles/utility'

import BaseLayout from '~layouts/BaseLayout'
import Icon from '~components/Icon'
import Hero, {
  HeroContent,
  HeroTitleWrapper,
  HeroTitle,
  HeroTitleSub,
} from '~partials/Hero'
import {
  ReliabilityCalculatorProvider,
  ReliabilityCalculatorContext,
} from '~partials/reliability-calculator/Context'
import Step1 from '~partials/reliability-calculator/Step1'
import Step2 from '~partials/reliability-calculator/Step2'
import Step3 from '~partials/reliability-calculator/Step3'
import Step4 from '~partials/reliability-calculator/Step4'
import Step5 from '~partials/reliability-calculator/Step5'
import Step6 from '~partials/reliability-calculator/Step6'
import Step7 from '~partials/reliability-calculator/Step7'
import Step8 from '~partials/reliability-calculator/Step8'
import Step9 from '~partials/reliability-calculator/Step9'
import Step10 from '~partials/reliability-calculator/Step10'
import Step11 from '~partials/reliability-calculator/Step11'
import gradeMascot from '~media/reliability-calculator/grade-mascot.svg'
import { StylesHeaderVariables } from '~partials/header/UI'
import HelmetSEO from '~partials/global/HelmetSEO'
import {
  getGrade,
  ReliabilityCell,
  ReliabilityHelp,
  ReliabilityHelpTrigger,
  StylesRC,
} from '~partials/reliability-calculator/UI'

const getQueryArgs = (search) => {
  const { results } = qs.parse(search, {
    ignoreQueryPrefix: true,
  })

  if (!isEmpty(results)) {
    try {
      const decodedURL = window.atob(results)
      const parsedURL = qs.parse(decodedURL)

      // If we have all of our query params; advance to cell 10
      if (
        parsedURL.step1 &&
        parsedURL.step2 &&
        parsedURL.step3 &&
        parsedURL.step4 &&
        parsedURL.step5 &&
        parsedURL.step6 &&
        parsedURL.step7 &&
        parsedURL.step8 &&
        parsedURL.step9
      ) {
        return parsedURL
      }
    } catch {
      return {}
    }
  }

  return {}
}

const ReliabilityProgress = (props) => {
  const theme = useTheme()

  return (
    <div
      css={{
        position: 'absolute',
        top: StylesHeaderVariables(theme).height,
        ...tealGreen(theme),
        left: 0,
        zIndex: 2,
        height: 4,
        transition: `width ${theme.timing.long} ${theme.transition.base}`,

        '&::before': {
          content: "''",
          position: 'absolute',
          left: 0,
          top: -1,
          background: rgba(theme.contrast.base, 0.25),
          height: 1,
          width: '100vw',
        },
      }}
      {...props}
    />
  )
}

// This is relative to the amount of "steps/questions" we have for the progress bar; not necessarily the actual amount of cells in the slider
const cells = 9

const Intro = ({ setActiveCell }) => {
  const theme = useTheme()

  const [helpOpen, toggleHelpOpen] = useState(false)

  return (
    <ReliabilityCell>
      {helpOpen ? (
        <ReliabilityHelp title="Tip" toggleHelpOpen={toggleHelpOpen}>
          <p css={{ textAlign: 'center' }}>
            Throughout the calculator, click on underlined terms to get more
            information about them.
          </p>
        </ReliabilityHelp>
      ) : (
        <HeroTitleWrapper className="u-contrast" bottomSpace>
          <HeroTitle
            size="large"
            css={{
              marginBottom: theme.space.large,
            }}
          >
            Get your{' '}
            <ReliabilityHelpTrigger onClick={() => toggleHelpOpen(!helpOpen)}>
              reliability grade.
            </ReliabilityHelpTrigger>
          </HeroTitle>
          <HeroTitleSub>
            <p>
              Understanding the relative reliability of your different services
              helps you prioritize efforts to make them more resilient. Use this
              assessment to rank the overall reliability of each of your
              services and get action items to improve them.
            </p>
            <p>
              Each time you use the calculator, you'll be asked a series of
              questions about <strong>a specific service</strong>. Ready to get
              started?
            </p>
          </HeroTitleSub>
        </HeroTitleWrapper>
      )}

      <div css={{ textAlign: 'center' }}>
        <Button
          type="primary"
          onClick={() => {
            setActiveCell(1)
          }}
        >
          Start
          <span
            css={{
              [theme.mq.xsmall__max]: {
                display: 'none',
              },
            }}
          >
            {' '}
            the assessment
          </span>
        </Button>
      </div>
    </ReliabilityCell>
  )
}

Intro.propTypes = {
  setActiveCell: PropTypes.func.isRequired,
}

const ReliabilityGrade = () => {
  const theme = useTheme()

  const {
    state: { step1, step2, step3, step4, step5, step6, step7, step8, step9 },
  } = useContext(ReliabilityCalculatorContext)

  const grade = getGrade(
    step1,
    step2,
    step3,
    step4,
    step5,
    step6,
    step7,
    step8,
    step9
  )

  const xOffset = 78

  return (
    <div
      css={{
        position: 'absolute',
        bottom: 0,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 2,
        background: `url(${gradeMascot}) 0 44px no-repeat`,
        backgroundSize: '108px 79px',
        paddingLeft: xOffset,
        paddingRight: xOffset,
      }}
    >
      <div
        css={{
          display: 'grid',
          alignItems: 'center',
          gridTemplateColumns: 'auto 1fr',
          background: theme.color.panel.base,
          borderTopLeftRadius: theme.borderRadius.base,
          borderTopRightRadius: theme.borderRadius.base,
        }}
      >
        <div
          css={{
            ...theme.headingPreset.h1,
            paddingTop: theme.space.small,
            paddingRight: theme.space.large,
            paddingBottom: theme.space.small + theme.space.xsmall,
            paddingLeft: theme.space.large,
            borderRight: theme.border.base,
            position: 'relative',
            zIndex: 1,
            background: theme.color.light.base,
            borderTopLeftRadius: theme.borderRadius.base,
          }}
        >
          <span css={StylesRC(theme).underline}>{grade}</span>
        </div>
        <div
          css={{
            paddingLeft: theme.space.base,
            paddingRight: theme.space.base,
            ...theme.text.small,
            ...text.expanded(theme),
            fontWeight: theme.fontWeight.bold,
          }}
        >
          Current Grade
        </div>
      </div>
    </div>
  )
}

const ReliabilityCalculatorNav = ({ activeCell, setActiveCell }) => {
  const theme = useTheme()

  const {
    state: { step1, step2, step3, step4, step5, step6, step7, step8, step9 },
  } = useContext(ReliabilityCalculatorContext)

  const checkActiveCell = () => {
    // Checking against empty string and null since 0 can be a valid answer
    switch (activeCell) {
      case 1:
        return step1 === '' || step1 === null
      case 2:
        return step2 === '' || step2 === null
      case 3:
        return step3 === '' || step3 === null
      case 4:
        return step4 === '' || step4 === null
      case 5:
        return step5 === '' || step5 === null
      case 6:
        return step6 === '' || step6 === null
      case 7:
        return step7 === '' || step7 === null
      case 8:
        return step8 === '' || step8 === null
      case 9:
        return step9 === '' || step9 === null
      default:
        return false
    }
  }

  return (
    <nav
      css={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: theme.space.base,
        marginTop: theme.space.xlarge,

        '> *:last-of-type': {
          textAlign: 'right',
        },
      }}
      className="u-contrast"
    >
      <div>
        <Button onClick={() => setActiveCell(activeCell - 1)}>
          <Icon
            css={{
              color: theme.color.primary.base,
              marginRight: theme.space.small,
            }}
            size="large"
            icon="caret-left"
          />{' '}
          Prev
        </Button>
      </div>
      <div>
        <Button
          disabled={checkActiveCell()}
          onClick={() => setActiveCell(activeCell + 1)}
        >
          Next{' '}
          <Icon
            css={{
              color: theme.color.primary.base,
              marginLeft: theme.space.small,
            }}
            size="large"
            icon="caret-right"
            disabled={activeCell === cells}
          />
        </Button>
      </div>
    </nav>
  )
}

ReliabilityCalculatorNav.propTypes = {
  activeCell: PropTypes.number.isRequired,
  setActiveCell: PropTypes.func.isRequired,
}

const ReliabilityCalculator = ({ location }) => {
  const theme = useTheme()

  // If we get valid query args, that means they are visiting a permalink with existing data; so we can forward them off to the results (and ultimately since we'll hide the form) recommendations area
  const [activeCell, setActiveCell] = useState(0)

  useEffect(() => {
    if (!isEmpty(getQueryArgs(location.search))) {
      setActiveCell(11)
    }
  }, [location.search])

  useUpdateEffect(() => {
    // We only stay on this cell for a couple seconds to "calculate" the score before we move onto the form
    if (activeCell === 10) {
      setTimeout(() => {
        setActiveCell(11)
      }, 2000)
    }
  }, [activeCell])

  return (
    <BaseLayout
      headerProps={{
        sticky: false,
        showNav: false,
        logoAside: (
          <Fragment>
            <div
              css={{
                width: 2,
                height: '100%',
                background: theme.contrast.base,
                opacity: theme.opacity.base,
              }}
            />
            <div css={{ fontWeight: theme.fontWeight.bold }}>
              Reliability Calculator
            </div>
          </Fragment>
        ),
      }}
      noFooter
    >
      <ReliabilityCalculatorProvider>
        <Fragment>
          <HelmetSEO
            title="Reliability Calculator"
            description="Understanding the relative reliability of your different services helps you prioritize efforts to make them more resilient. You can use this calculator to rank the overall reliability of each of your systems and then get action items to improve them."
            image="https://res.cloudinary.com/gremlin/image/upload/t_default/v1572884103/reliability-calculator-og.png"
          />
          <ReliabilityProgress
            style={{
              width: `${
                (activeCell / cells > 1 ? 1 : activeCell / cells) * 100
              }%`,
            }}
          />
          <Hero
            contrast={false}
            css={{
              background: StylesRC(theme).background,
              paddingBottom: StylesRC(theme).gradeSize,
              minHeight: '100vh !important',
            }}
          >
            <HeroContent>
              <Container>
                {activeCell === 0 && <Intro setActiveCell={setActiveCell} />}
                {activeCell === 1 && <Step1 />}
                {activeCell === 2 && <Step2 />}
                {activeCell === 3 && <Step3 />}
                {activeCell === 4 && <Step4 />}
                {activeCell === 5 && <Step5 />}
                {activeCell === 6 && <Step6 />}
                {activeCell === 7 && <Step7 />}
                {activeCell === 8 && <Step8 />}
                {activeCell === 9 && <Step9 />}
                {activeCell === 10 && <Step10 />}
                {activeCell === 11 && (
                  <Step11
                    location={location}
                    queryArgs={
                      !isEmpty(getQueryArgs(location.search))
                        ? getQueryArgs(location.search)
                        : {}
                    }
                  />
                )}
                {/* We're hiding the nav on the first, and last two slides */}
                {activeCell > 0 && activeCell !== 10 && activeCell !== 11 && (
                  <ReliabilityCalculatorNav
                    activeCell={activeCell}
                    setActiveCell={setActiveCell}
                  />
                )}
              </Container>
            </HeroContent>
          </Hero>
          {activeCell > 1 && activeCell !== 10 && activeCell !== 11 && (
            <ReliabilityGrade />
          )}
        </Fragment>
      </ReliabilityCalculatorProvider>
      {/* We need this static form here even though it's done in Step11 since Netlify requires the static HTML to be present on the page in order to parse data correctly 🤷‍♂️ */}
      <form
        css={misc.hide}
        name="reliability-calculator"
        data-netlify="true"
        data-netlify-honeypot="bot-field"
      >
        <input type="hidden" name="form-name" value="reliability-calculator" />
        <input type="email" name="email" />
        <input type="text" name="firstName" />
        <input type="text" name="lastName" />
        <input type="text" name="company" />
        <input type="text" name="jobTitle" />
        <input type="text" name="url" />
        <input type="text" name="bot-field" autoComplete="new-password" />
      </form>
    </BaseLayout>
  )
}

ReliabilityCalculator.propTypes = {
  location: PropTypes.object.isRequired,
}

export default ReliabilityCalculator
